// แคตตาล็อกรายงานในเมนู Discover
//
// Discover เป็นสารบัญ ไม่ใช่เมนู — รายงานบางตัวเป็นระบบคนละโดเมน บางตัวเป็นหน้า
// ของ Inbox Center ที่เราฝังผ่าน proxy อยู่แล้ว สิ่งที่เหมือนกันคือ "หนักและไม่ได้
// เปิดทุกวัน" จึงมารวมอยู่ที่นี่และโหลดจริงตอนกดเข้าไปเท่านั้น
//
// เพิ่มรายงานใหม่ได้ที่ REPORTS ข้างล่างที่เดียว ไม่ต้องแตะหน้าจอ
import type { Brand } from "@contracts/api";
import type { MenuIconName } from "@shared/ui/menu-icons";

/**
 * Competitor Content Report — กินรายชื่อเพจจาก /report-feed ของเรา
 * (ดู server/services/report-feed.service.ts) จึงใช้ id ของ Product Group ชุดเดียวกัน
 *
 * ฝังผ่าน proxy ของเราเอง ไม่ใช่ชี้ไปโดเมนเขาตรง ๆ เพื่อให้ปุ่มเลือกกลุ่มในหน้าเขา
 * เหลือเฉพาะกลุ่มที่เปิดอยู่ (ดู server/middleware/fbdash-proxy.ts)
 */
const fbEngagementUrl = (groupId: string) =>
  `/fbdash/${encodeURIComponent(groupId)}/?group=${encodeURIComponent(groupId)}`;

export type DiscoverReport = {
  /** ใช้เป็น slug ใน URL: /brand/:id/discover/:slug */
  slug: string;
  title: string;
  desc: string;
  /** ป้ายสั้น ๆ บอกว่าข้อมูลมาจากไหน */
  tag: string;
  icon: MenuIconName;
  /** ระบบนอก — ฝังใน iframe และเป็นลิงก์ "เปิดในแท็บใหม่" ตัวเดียวกัน */
  url?: (brand: Brand) => string;
  /** หน้าของ Inbox Center ที่ฝังผ่าน proxy เช่น analytics.html (ใช้แทน url) */
  inboxPage?: string;
  /**
   * รายงานที่ทำจากข้อมูลระดับ Product Group เท่านั้น — หน้า Discover จะแตกเป็น
   * กล่องละกลุ่มตามสิทธิ์ของคนที่เปิด
   */
  groupOnly?: boolean;
  /** ต้องเปิด feature "report" ของแบรนด์ไว้ถึงจะมีข้อมูล */
  needsReport?: boolean;
  /** ต้องเปิด feature "content" ของแบรนด์ไว้ถึงจะมีข้อมูล */
  needsContent?: boolean;
};

export const REPORTS: DiscoverReport[] = [
  {
    slug: "facebook-engagement",
    title: "Facebook Engagement Dashboard",
    desc: "เทียบยอด engagement รายเดือนของแบรนด์เรากับคู่แข่งในกลุ่ม จากเพจที่ตั้งไว้ในหน้า Brand & Competitors",
    tag: "Facebook · รายเดือน",
    icon: "trend",
    groupOnly: true,
    url: (brand) => fbEngagementUrl(brand.id),
  },
  {
    slug: "content-calendar",
    title: "Content Calendar",
    desc: "ปฏิทินโพสต์ของเพจแบรนด์นี้ — เห็นว่าแต่ละวันลงอะไรไป พร้อมยอดตอบรับของโพสต์นั้น",
    tag: "Inbox Center · รายเดือน",
    icon: "layout",
    inboxPage: "comments.html",
    needsContent: true,
  },
  {
    slug: "facebook-inbox",
    title: "Inbox Performance Report",
    desc: "สถิติการตอบแชทของเพจแบรนด์นี้ — จำนวนห้อง เวลาตอบ และสัดส่วนที่ตอบทัน",
    tag: "Inbox Center · เรียลไทม์",
    icon: "chat",
    inboxPage: "analytics.html",
    needsReport: true,
  },
];

export const findReport = (slug?: string): DiscoverReport | undefined =>
  REPORTS.find((r) => r.slug === slug);

/** เหตุผลที่รายงานนี้เปิดจากแบรนด์ที่กำลังดูอยู่ไม่ได้ — null = เปิดได้ */
export function blockedReason(report: DiscoverReport, brand: Brand): string | null {
  if (report.groupOnly && brand.kind !== "group") {
    return "รายงานนี้ทำจากรายชื่อคู่แข่งของ Product Group — เลือกกลุ่มที่ต้องการได้ในหน้า Discover";
  }
  if (report.needsReport && brand.features?.report === false) {
    return "แบรนด์นี้ปิดการดึงรายงานไว้ — เปิดได้ที่ Setting → Channels & Inbox";
  }
  if (report.needsContent && brand.features?.content === false) {
    return "แบรนด์นี้ปิดการดึงเนื้อหาไว้ — เปิดได้ที่ Setting → Channels & Inbox";
  }
  return null;
}
